import { useEffect, useState } from "react";
import { Admin } from "@/lib/api";
import { AdminLayout } from "./AdminPages";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Save } from "lucide-react";

interface Milestone {
  day: number;
  bonus: number;
  label?: string;
}

interface CheckinRules {
  enabled: boolean;
  base_points: number;
  increment_per_day: number;
  max_daily_points: number;
  cycle_days: number;
  reset_on_miss: boolean;
  milestones: Milestone[];
}

const DEFAULT: CheckinRules = {
  enabled: true,
  base_points: 1,
  increment_per_day: 1,
  max_daily_points: 7,
  cycle_days: 7,
  reset_on_miss: true,
  milestones: [
    { day: 7, bonus: 5, label: "连续 7 天" },
    { day: 30, bonus: 50, label: "连续 30 天" },
  ],
};

export function AdminCheckinPage() {
  const [rules, setRules] = useState<CheckinRules>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    Admin.getSetting("checkin")
      .then((r) => { if (r?.value) setRules({ ...DEFAULT, ...r.value }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setMsg("");
    try {
      await Admin.setSetting("checkin", rules);
      setMsg("✓ 已保存");
      setTimeout(() => setMsg(""), 2000);
    } catch (e: any) {
      setMsg("× " + (e?.message || "保存失败"));
    }
  };

  const addMilestone = () => setRules({ ...rules, milestones: [...rules.milestones, { day: 0, bonus: 0, label: "" }] });
  const updateMilestone = (i: number, m: Partial<Milestone>) => {
    const next = [...rules.milestones];
    next[i] = { ...next[i]!, ...m };
    setRules({ ...rules, milestones: next });
  };
  const removeMilestone = (i: number) => setRules({ ...rules, milestones: rules.milestones.filter((_, idx) => idx !== i) });

  if (loading) return <AdminLayout><div className="p-6 text-sm text-muted-foreground">加载中…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold">签到积分规则</h1>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={rules.enabled}
                  onChange={(e) => setRules({ ...rules, enabled: e.target.checked })} />
                启用签到
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              用户每日可签到 1 次。第 N 天的基础积分 = <code className="px-1 rounded bg-muted">base + increment × ((N-1) mod cycle)</code>，封顶为最大单日积分。达到里程碑（如连续 7 天、30 天）时额外奖励。
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label>基础积分 base_points</Label>
                <Input type="number" value={rules.base_points}
                  onChange={(e) => setRules({ ...rules, base_points: +e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>每日递增 increment</Label>
                <Input type="number" value={rules.increment_per_day}
                  onChange={(e) => setRules({ ...rules, increment_per_day: +e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>单日封顶 max</Label>
                <Input type="number" value={rules.max_daily_points}
                  onChange={(e) => setRules({ ...rules, max_daily_points: +e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>周期天数 cycle_days</Label>
                <Input type="number" value={rules.cycle_days}
                  onChange={(e) => setRules({ ...rules, cycle_days: +e.target.value })} />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={rules.reset_on_miss}
                onChange={(e) => setRules({ ...rules, reset_on_miss: e.target.checked })} />
              漏签后重置连续天数（关闭则保留历史最长）
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">连续签到里程碑</h2>
              <Button size="sm" variant="outline" onClick={addMilestone}>
                <Plus className="h-4 w-4 mr-1" /> 新增里程碑
              </Button>
            </div>
            {rules.milestones.length === 0 && (
              <p className="text-sm text-muted-foreground">暂无里程碑，添加后用户在连续签到达成时一次性获得额外奖励。</p>
            )}
            <div className="space-y-2">
              {rules.milestones.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">连续天数</Label>
                    <Input type="number" value={m.day} onChange={(e) => updateMilestone(i, { day: +e.target.value })} />
                  </div>
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">额外积分</Label>
                    <Input type="number" value={m.bonus} onChange={(e) => updateMilestone(i, { bonus: +e.target.value })} />
                  </div>
                  <div className="col-span-5 space-y-1">
                    <Label className="text-xs">说明</Label>
                    <Input value={m.label || ""} onChange={(e) => updateMilestone(i, { label: e.target.value })} placeholder="可选" />
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeMilestone(i)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button onClick={save}><Save className="h-4 w-4 mr-1" /> 保存规则</Button>
          {msg && <span className="text-sm text-muted-foreground">{msg}</span>}
        </div>
      </div>
    </AdminLayout>
  );
}
